"use client";

import React from "react";

import { Checkbox } from "./Checkbox";

export interface AdvancedCountsFilterProps {
  attributeCounts?: Map<string, Map<string, number>>;
  selectedAttributes?: Map<string, Set<string>>;
  onToggle?: (attributeHandle: string, valueHandle: string) => void;
  label?: string;
  className?: string;
}

function trimAttributePrefix(handle: string, value: string): string {
  const prefix = `${handle}__`;
  return value.startsWith(prefix) ? value.substring(prefix.length) : value;
}

function formatFilterLabel(handle: string, value: string): string {
  return trimAttributePrefix(handle, value)
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatFacetDisplayName(name: string): string {
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function ChevronDown({ isExpanded }: { isExpanded: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)" }}
    >
      <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ExpandAllToggle({ checked, onChange }: { checked: boolean; onChange: () => void }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={checked ? "Fold all advanced facets" : "Expand all advanced facets"}
      onClick={(event) => {
        event.stopPropagation();
        onChange();
      }}
      className="inline-flex items-center gap-1.5 text-[10.5px] font-medium text-muted-foreground transition-colors hover:text-foreground"
    >
      <span>Expand all</span>
      <span
        className={`relative h-4 w-7 rounded-full border transition-colors ${
          checked ? "border-foreground bg-foreground" : "border-muted-foreground/40 bg-transparent"
        }`}
      >
        <span
          className={`absolute left-[3px] top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full transition-transform ${
            checked ? "translate-x-3 bg-background" : "translate-x-0 bg-muted-foreground"
          }`}
        />
      </span>
    </button>
  );
}

export function AdvancedCountsFilter({
  attributeCounts,
  selectedAttributes = new Map(),
  onToggle,
  label = "ADVANCED",
  className = "",
}: AdvancedCountsFilterProps) {
  const [sectionExpanded, setSectionExpanded] = React.useState(true);
  const [expandedAttributes, setExpandedAttributes] = React.useState<Set<string>>(new Set());

  const sortedAttributes = React.useMemo(() => {
    if (!attributeCounts || attributeCounts.size === 0) return [];
    return Array.from(attributeCounts.entries())
      .map(([handle, valueCounts]) => {
        const values = Array.from(valueCounts.entries())
          .filter(([, count]) => count > 0)
          .sort(([, a], [, b]) => b - a);
        return { handle, values };
      })
      .filter((attr) => attr.values.length > 0)
      .sort((a, b) => a.handle.localeCompare(b.handle));
  }, [attributeCounts]);

  if (sortedAttributes.length === 0) {
    return null;
  }

  const toggleAttributeExpanded = (handle: string) => {
    setExpandedAttributes((prev) => {
      const next = new Set(prev);
      if (next.has(handle)) next.delete(handle);
      else next.add(handle);
      return next;
    });
  };

  const allAttributesExpanded =
    sortedAttributes.length > 0 &&
    sortedAttributes.every(({ handle }) => expandedAttributes.has(handle));

  const toggleAllAttributesExpanded = () => {
    if (allAttributesExpanded) {
      setExpandedAttributes(new Set());
      return;
    }

    setExpandedAttributes(new Set(sortedAttributes.map(({ handle }) => handle)));
  };

  return (
    <div className={className}>
      <div
        style={{
          display: "flex",
          height: "41.974px",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
          cursor: "pointer",
        }}
        onClick={() => setSectionExpanded((prev) => !prev)}
      >
        <span
          style={{
            fontSize: "10.5px",
            lineHeight: "14px",
            letterSpacing: "0.6173px",
            textTransform: "uppercase",
            color: "#000000",
            fontWeight: 500,
          }}
        >
          {label}
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          {sectionExpanded && (
            <ExpandAllToggle
              checked={allAttributesExpanded}
              onChange={toggleAllAttributesExpanded}
            />
          )}
          <ChevronDown isExpanded={sectionExpanded} />
        </div>
      </div>

      {sectionExpanded && (
        <div>
          {sortedAttributes.map(({ handle, values }) => {
            const isExpanded = expandedAttributes.has(handle);
            return (
              <React.Fragment key={handle}>
                <div
                  style={{
                    display: "flex",
                    height: "34px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    cursor: "pointer",
                  }}
                  onClick={() => toggleAttributeExpanded(handle)}
                >
                  <span
                    style={{
                      fontSize: "10.5px",
                      lineHeight: "14px",
                      letterSpacing: "0.6173px",
                      textTransform: "uppercase",
                      color: "#6A7282",
                      fontWeight: 500,
                    }}
                  >
                    {formatFacetDisplayName(handle)}
                  </span>
                  <ChevronDown isExpanded={isExpanded} />
                </div>

                {isExpanded && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      paddingBottom: "4px",
                      paddingTop: "4px",
                      gap: "4px",
                    }}
                  >
                    {values.map(([value, count]) => {
                      const isSelected = selectedAttributes.get(handle)?.has(value) ?? false;
                      return (
                        <div
                          key={value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            height: "24px",
                            cursor: "pointer",
                          }}
                          onClick={() => onToggle?.(handle, value)}
                        >
                          <div>
                            <Checkbox
                              checked={isSelected}
                              onChange={() => onToggle?.(handle, value)}
                            />
                          </div>
                          <div
                            style={{
                              marginLeft: "23.988px",
                              flex: 1,
                              minWidth: 0,
                              textAlign: "left",
                            }}
                          >
                            <span className="text-sm text-foreground">
                              {formatFilterLabel(handle, value)}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground tabular-nums">
                            {count.toLocaleString()}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      <div
        style={{
          width: "100%",
          height: "0.882px",
          borderBottom: "0.882px solid rgba(0, 0, 0, 0.1)",
        }}
      />
    </div>
  );
}
